'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useDashboard } from '@/components/dashboard/DashboardContext';
import ServerList from '../navigation/ServerList';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

// 아이콘 import
import { Home, Plus, Compass, RefreshCw, Unlock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface SidebarProps {
  isInDrawer?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isInDrawer = false }) => {
  const { user, login } = useAuth();
  const { currentGuildId, closeSidebar, refreshGuilds } = useDashboard();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pathname = usePathname();

  // 현재 경로가 대시보드 홈인지 확인
  const isDashboardHome = pathname === '/dashboard';
  // 미인증 상태이면서 홈인지 확인
  const isUnauthenticatedHome = isDashboardHome && user?.verified == null;

  const handleVerifyIdentity = () => {
    login('/dashboard/user/settings'); // 현재 페이지로 돌아오도록 리디렉션 경로 설정
    toast.info('Discord 인증', {
      description: 'Discord 계정 인증 페이지로 이동합니다.',
      duration: 3000,
    });
  };

  // 새로고침 핸들러
  const handleRefresh = async (e: React.MouseEvent) => {
    // 상위 요소의 클릭 이벤트 전파 방지
    e.preventDefault();
    e.stopPropagation();

    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      await refreshGuilds();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="w-[72px] flex-shrink-0 flex flex-col items-center py-2 bg-background border-r border-border h-full">
      {/* 대시보드 홈 버튼 */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              href="/dashboard"
              onClick={(e) => {
                // 홈 화면에서 미인증 사용자일 경우 로그인 실행
                if (isUnauthenticatedHome) {
                  e.preventDefault(); // 기본 링크 동작 중지
                  handleVerifyIdentity();
                  return;
                }
                
                // 홈 화면에서는 항상 새로고침 기능 실행
                if (isDashboardHome && !isRefreshing) {
                  handleRefresh(e);
                  return;
                }

                if (isInDrawer) {
                  closeSidebar();
                }
              }}
              className={`min-w-[48px] min-h-[48px] rounded-full flex items-center justify-center mb-2
                ${currentGuildId
                  ? 'hover:bg-accent bg-accent/30 text-foreground dark:text-foreground'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90 dark:text-primary-foreground'}`}
            >
              {/* 미인증 대시보드 홈에서는 잠금해제 아이콘, 인증된 대시보드 홈에서는 새로고침 아이콘, 다른 페이지에서는 홈 아이콘 표시 */}
              {isUnauthenticatedHome ? (
                <Unlock className="h-5 w-5" />
              ) : isDashboardHome ? (
                <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              ) : (
                <Home className="h-5 w-5" />
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="dark:bg-popover dark:text-popover-foreground">
            {isUnauthenticatedHome ? '서버 더보기' : isDashboardHome ? '서버 목록 새로고침' : '대시보드 홈'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* 서버 리스트 */}
      <ScrollArea className="w-full flex-1 overflow-x-hidden">
        <div className="flex flex-col items-center gap-1">
          <ServerList preserveDrawer={isInDrawer} />
        </div>
      </ScrollArea>

      <Separator className="w-4/5 my-1" />

      {/* 서버에 카미봇 추가하기 버튼 */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href="https://discord.com/oauth2/authorize?client_id=1019061779357245521&permissions=3149051&scope=bot%20applications.commands"
              target="_blank"
              rel="noopener noreferrer"
              className="w-[48px] h-[48px] rounded-full flex items-center justify-center bg-accent/30 hover:bg-accent text-success dark:text-success"
            >
              <Plus className="h-5 w-5" />
            </a>
          </TooltipTrigger>
          <TooltipContent side="right" className="dark:bg-popover dark:text-popover-foreground">
            서버에 카미봇 추가하기
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* 카미봇 서버 탐색하기 버튼 */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href="https://discord.gg/zr9BdyxRXM"
              target="_blank"
              rel="noopener noreferrer"
              className="w-[48px] h-[48px] rounded-full flex items-center justify-center mt-1 bg-accent/30 hover:bg-accent text-foreground dark:text-foreground"
            >
              <Compass className="h-5 w-5" />
            </a>
          </TooltipTrigger>
          <TooltipContent side="right" className="dark:bg-popover dark:text-popover-foreground">
            카미봇 지원 서버
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default Sidebar;